import { useEffect, useRef } from 'react';

import { useDispatch } from 'react-redux';

import { setAuthed } from '@/redux/slice/login/loginSlice';

const CHANNEL = 'emr_auth_sync';

// Module-level channel reference — allows broadcastAuthLogout to be called from non-hook
// contexts (e.g. useAuth.logout) without coupling those callers to React lifecycle.
let _channel: BroadcastChannel | null = null;

// Only broadcasts when this tab still holds a token, breaking any re-broadcast cascade:
// once the token is cleared, this becomes a no-op and the LOGOUT chain terminates.
export function broadcastAuthLogout(): void {
  if (sessionStorage.getItem('access_token')) {
    _channel?.postMessage({ type: 'LOGOUT' });
  }
}

interface TabSyncCallbacks {
  onTokenReceived?: (rawToken: string) => void;
  onLogoutReceived?: () => void;
}

export function useTabSync(callbacks?: TabSyncCallbacks): void {
  const dispatch = useDispatch();
  const callbacksRef = useRef(callbacks);

  // Keep ref current without triggering the channel effect to re-run
  useEffect(() => {
    callbacksRef.current = callbacks;
  });

  useEffect(() => {
    const channel = new BroadcastChannel(CHANNEL);
    _channel = channel;

    channel.onmessage = (event: MessageEvent): void => {
      if (event.data?.type === 'TOKEN_REQUEST') {
        const token = sessionStorage.getItem('access_token');
        if (token) {
          channel.postMessage({ type: 'TOKEN_RESPONSE', token });
        }
      } else if (event.data?.type === 'LOGOUT') {
        callbacksRef.current?.onLogoutReceived?.();
      }
    };

    // If this tab has no token, ask a sibling tab for one
    if (!sessionStorage.getItem('access_token')) {
      channel.postMessage({ type: 'TOKEN_REQUEST' });

      const handler = (event: MessageEvent): void => {
        if (event.data?.type === 'TOKEN_RESPONSE' && event.data.token) {
          sessionStorage.setItem('access_token', event.data.token);
          dispatch(setAuthed({ authed: true, token: event.data.token }));
          callbacksRef.current?.onTokenReceived?.(event.data.token);
          channel.removeEventListener('message', handler);
        }
      };
      channel.addEventListener('message', handler);

      // No sibling responded — fall through to login redirect
      const timer = setTimeout(() => channel.removeEventListener('message', handler), 300);

      return () => {
        clearTimeout(timer);
        channel.close();
        _channel = null;
      };
    }

    return () => {
      channel.close();
      _channel = null;
    };
  }, [dispatch]);
}
